import {
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  Collapse,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
// Using text instead of icon for delete button
import { useState } from "react";
import { useUsersManager } from "../Hooks/useUsersManager";
import {
  UserFormat,
  UserCredentials,
} from "../../common/models/schema/UserFormat";
import { UserPermissionGroup } from "../../common/models/schema/UserPermissionGroup";

const UsersManagerPage = () => {
  const {
    users,
    setUsers,
    loading,
    error,
    createUser,
    createUserError,
    changeUserPermissionGroup,
    deleteUser,
  } = useUsersManager();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);

  const handlePermissionGroupChange = async (
    email: string,
    newGroup: UserPermissionGroup,
  ) => {
    await changeUserPermissionGroup(email, newGroup);
  };

  const handleCreateUser = async () => {
    if (!newUser.email.trim() || !newUser.password.trim()) {
      return;
    }

    try {
      setCreateLoading(true);
      await createUser(newUser as UserCredentials);
      setNewUser({ email: "", password: "" });
      setShowCreateForm(false);
    } catch (error) {
      console.error("Error creating user:", error);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setNewUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleDeleteClick = (email: string) => {
    setUserToDelete(email);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      try {
        await deleteUser(userToDelete);
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const getPermissionGroupLabel = (
    group: UserPermissionGroup | null,
  ): string => {
    switch (group) {
      case UserPermissionGroup.Admin:
        return "Admin Group";
      case UserPermissionGroup.Default:
        return "Default Group";
      default:
        return "Default Group";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Users Manager
        </Typography>
        <Button
          variant="contained"
          onClick={() => setShowCreateForm(!showCreateForm)}
          sx={{ ml: 2 }}
        >
          Create User
        </Button>
      </Box>

      <Collapse in={showCreateForm}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create New User
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <TextField
                label="Email"
                value={newUser.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <TextField
                label="Password"
                type="text"
                value={newUser.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                onClick={handleCreateUser}
                disabled={
                  !newUser.email.trim() ||
                  !newUser.password.trim() ||
                  createLoading
                }
              >
                {createLoading ? <CircularProgress size={20} /> : "Create"}
              </Button>
              {createUserError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {createUserError}
                </Alert>
              )}
            </Box>
          </CardContent>
        </Card>
      </Collapse>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && users.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <List>
            {users.map((user: UserFormat, index: number) => (
              <Box key={user.email || index}>
                <ListItem sx={{ py: 2 }}>
                  <ListItemText primary={user.email} sx={{ minWidth: 200 }} />
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Permission Group</InputLabel>
                    <Select
                      value={user.groups || UserPermissionGroup.Default}
                      label="Permission Group"
                      onChange={(e) =>
                        handlePermissionGroupChange(
                          user.email,
                          e.target.value as UserPermissionGroup,
                        )
                      }
                    >
                      <MenuItem value={UserPermissionGroup.Default}>
                        {getPermissionGroupLabel(UserPermissionGroup.Default)}
                      </MenuItem>
                      <MenuItem value={UserPermissionGroup.Admin}>
                        {getPermissionGroupLabel(UserPermissionGroup.Admin)}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    onClick={() => handleDeleteClick(user.email)}
                    color="error"
                    variant="outlined"
                    size="small"
                    sx={{ ml: 2 }}
                  >
                    Delete
                  </Button>
                </ListItem>
                {index < users.length - 1 && <Divider />}
              </Box>
            ))}
          </List>
        </Box>
      )}

      {!loading && !error && users.length === 0 && (
        <Typography variant="body1" sx={{ mt: 3 }}>
          No users found.
        </Typography>
      )}

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete user "{userToDelete}"? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersManagerPage;
